import type { StorefrontProductDetail } from '@erp/shared';
import i18n from 'i18next';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { create } from 'zustand';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { type CartLine, cartKey, useCartStore } from '../../lib/cartStore.js';
import { publicApi } from '../../lib/publicApi.js';

import type { BuyerPricing } from './buyerPricing.js';

/**
 * Keeps the cart in ONE currency — the shopper's.
 *
 * A cart is priced when it is filled, but the basis can change underneath it:
 * signing in puts the shopper on a customer group (possibly priced in another
 * currency), signing out drops them back to store prices. Rather than silently
 * mixing currencies — or nuking the cart — we RE-PRICE every line from the
 * server (the same personalized product endpoint the shopper saw) and drop only
 * the lines their group can't be priced for.
 */

/** One re-price pass per (store, currency) at a time. */
const inflight = new Set<string>();

/**
 * Global "a re-price is running" flag rather than per-component state: a pass can
 * start on any page (the moment the server reveals the shopper's group), and the
 * cart/checkout must wait for it wherever they mount.
 */
const useSyncBusy = create<{ busy: boolean; set(busy: boolean): void }>((set) => ({
  busy: false,
  set: (busy) => set({ busy }),
}));

/**
 * The buyer's current price for a cart line, mirroring the product page exactly:
 * an option (variant) sets the per-piece price, a packaging unit multiplies it.
 * `null` ⇒ this line cannot be priced for this shopper (gone, unpublished, or no
 * price in their group's currency — the server 404s those).
 */
async function repricedLine(slug: string, line: CartLine, currency: string): Promise<string | null> {
  try {
    const { data } = await publicApi().get<StorefrontProductDetail>(`/${slug}/products/${line.itemId}`);
    // The server stamps the currency it priced in; anything else is not ours.
    if (data.currencyCode !== currency) return null;
    const variant = line.itemVariantId ? data.variants.find((v) => v.id === line.itemVariantId) : undefined;
    if (line.itemVariantId && !variant) return null;
    const unit = line.itemUnitId ? data.units.find((u) => u.id === line.itemUnitId) : undefined;
    if (line.itemUnitId && !unit) return null;
    // A variant sold BY THE PACK is priced by the server — the client never sees
    // the discount rules, so it cannot reproduce them.
    const packOf = (u: NonNullable<typeof unit>): string => (u.price && Number(u.price) > 0 ? u.price : data.price);
    if (variant) {
      if (!unit) return variant.price;
      const pack = variant.unitPrices?.find((up) => up.unitId === unit.id);
      // Fallback for an out-of-date payload: the PACK's own price. Multiplying the
      // option's per-piece price by the factor (as this did) discards the carton's
      // bulk price and overcharges for what is usually just a colour.
      return pack ? pack.price : packOf(unit);
    }
    if (unit) return packOf(unit);
    return data.price;
  } catch {
    return null;
  }
}

/**
 * Move the cart onto `currency`, re-pricing every line from the server. Callable
 * from anywhere (it runs the moment a catalogue response reveals the shopper's
 * group), so a stale-currency cart is fixed BEFORE the next add — cartStore.add
 * would otherwise drop the mismatched lines without a word. No-op when the cart
 * already agrees, belongs to another store, or a pass is already running.
 */
export function reconcileCartCurrency(slug: string, currency: string): void {
  const cart = useCartStore.getState();
  if (cart.slug !== slug) return;
  // Only the lines from the older basis need a lookup; the rest already match.
  const snapshot = cart.items.filter((l) => l.currencyCode !== currency);
  if (snapshot.length === 0) return;
  const key = `${slug}|${currency}`;
  if (inflight.has(key)) return;
  inflight.add(key);
  useSyncBusy.getState().set(true);
  void Promise.all(snapshot.map(async (l) => ({ key: cartKey(l), price: await repricedLine(slug, l, currency) })))
    .then((results) => {
      const priced = new Map(results.filter((r) => r.price !== null).map((r) => [r.key, r.price as string]));
      let dropped = 0;
      useCartStore.setState((state) => {
        // Re-read the live cart: quantities may have moved while we fetched.
        if (state.slug !== slug) return state;
        dropped = 0;
        const next: CartLine[] = [];
        for (const l of state.items) {
          if (l.currencyCode === currency) { next.push(l); continue; }
          const price = priced.get(cartKey(l));
          if (price == null) { dropped += 1; continue; }
          next.push({ ...l, price, currencyCode: currency });
        }
        return { items: next };
      });
      // Runs outside React (any page can trigger it), so translate off the shared
      // i18next instance rather than a hook.
      if (dropped > 0) toast.info(i18n.t('cart.repricedDropped', { ns: 'cms', n: dropped }));
      else toast.info(i18n.t('cart.repriced', { ns: 'cms' }));
    })
    .finally(() => { inflight.delete(key); useSyncBusy.getState().set(false); });
}

/**
 * Page-side companion: makes sure the cart matches the shopper's basis even when
 * no catalogue call happened (a session that reloads straight onto /cart), and
 * reports `syncing` so the cart/checkout can hold their totals — and their
 * empty-cart redirect — until the cart is coherent again.
 */
export function useCartCurrencySync(buyer: BuyerPricing): { syncing: boolean } {
  const { slug } = useStorefront();
  const syncing = useSyncBusy((s) => s.busy);
  const cartSlug = useCartStore((s) => s.slug);
  const items = useCartStore((s) => s.items);
  const currency = buyer.currencyCode;
  // Only ever act on a server-confirmed basis: re-pricing on a guess would
  // rewrite a group customer's cart into store prices.
  const stale = buyer.authoritative && cartSlug === slug && items.some((l) => l.currencyCode !== currency);

  useEffect(() => {
    if (stale) reconcileCartCurrency(slug, currency);
  }, [stale, slug, currency]);

  return { syncing };
}
